// /src/pages/TradeCenter.jsx
import { useEffect, useState, useContext, useRef, useLayoutEffect, useCallback } from 'react';
import { collection, query, where, orderBy, onSnapshot, deleteDoc, doc, getDoc, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import Footer from '../components/Footer';

import { Link } from 'react-router-dom';
import { auth, db } from '../firebase';
import { UserContext } from '../contexts/UserContext';
import { getInitiatorPedalIds } from '../utils/tradeUtils';
import { useRegisterPullToRefresh } from '../hooks/useRegisterPullToRefresh';
import { scrollPageToTop } from '../utils/scrollPageToTop';
import { useReturnViewState, useRestoreScroll, OFFER_FLOW_PREFIXES, OFFERS_VIEW_KEY } from '../hooks/useReturnViewState';
import { useTradeCenterTab } from '../hooks/useTradeCenterTab';
import { visibleTradeCenterTab } from '../utils/tradeCenterTab';
import { useNotifications } from '../contexts/NotificationContext';
import { isListingInterestEnabled } from '../featureFlags';
import { isInterestFresh } from '../utils/listingInterestPolicy';
import { placeholderFor, SKETCH_PLACEHOLDER_URL } from '../utils/listingPlaceholder';
import OnboardingTip from '../components/OnboardingTip';
import useOnboardingTips from '../hooks/useOnboardingTips';

// Which notifications count as "seen" once the user lands on this page. Both
// offer directions live here (sent and received, trade and purchase), so seeing
// the list counts as seeing the alert — otherwise these only ever cleared by
// opening each offer one by one, or from the bell, which on native is only
// reachable from Home.
//
// Scoped deliberately: this page only loads offers in 'pending' or 'countered'.
// An accepted or declined offer is NOT rendered here, so marking those read
// would bury an outcome the user never saw. Accepted already clears on the
// Trades and Purchases pages, declined clears on the offer itself.
const isOfferPageNotification = (n) => {
  switch (n.type) {
    case 'offer_status_change':
    case 'purchase_offer_status_change':
      return n.metadata?.action === 'countered';
    case 'new_offer':
    case 'new_purchase_offer':
    case 'offer_comment':
    case 'purchase_offer_comment':
    case 'offer_viewed':
      return true;
    default:
      return false;
  }
};

// Renders an eye + "Seen" / "Not seen" indicator showing whether the
// recipient of the current offer state has viewed it. Used in both the
// sent and received offer lists. Returns null when:
//   - the offer is in a terminal status (no current recipient), or
//   - the offer is missing offerUpdatedAt (legacy doc).
// "Not seen" renders in light gray; "Seen" renders in green.
const SeenBadge = ({ offer }) => {
  // Recipient = whoever did NOT make the last offer/response action.
  // Status alone isn't sufficient because counter-counters keep the status
  // as 'countered' while flipping the direction.
  const actionEntries = (offer.history || []).filter(e => e.type === 'offer' || e.type === 'response');
  const lastActionFrom = actionEntries.length > 0 ? actionEntries[actionEntries.length - 1].from : null;
  const isActiveStatus = offer.status === 'pending' || offer.status === 'countered';
  const partyA = offer.offerType === 'purchase' ? offer.initiatorId : offer.fromUserId;
  const partyB = offer.offerType === 'purchase' ? offer.responderId : offer.toUserId;
  const recipientUid = (isActiveStatus && lastActionFrom)
    ? (lastActionFrom === partyA ? partyB : partyA)
    : null;
  if (!recipientUid) return null;
  const stateMs = offer.offerUpdatedAt?.toMillis?.() ?? 0;
  if (!(stateMs > 0)) return null;
  const viewedMs = offer.viewedAt?.[recipientUid]?.toMillis?.() ?? 0;
  const isSeen = viewedMs >= stateMs;
  return (
    <span className="offer-seen-badge" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem', color: isSeen ? '#16a34a' : '#9ca3af', fontWeight: 600, whiteSpace: 'nowrap' }}>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
      {isSeen ? 'Seen' : 'Not seen'}
    </span>
  );
};

// Tab + scroll memory for coming back from the other offer pages (see
// hooks/useReturnViewState.js for the rules).

const TradeCenter = () => {
  const { notifications, markAsRead } = useNotifications();
  const claimedNotificationIds = useRef(new Set());

  // Mark offer notifications as read when the user lands on the Offers page.
  useEffect(() => {
    const pending = notifications.filter(
      n => !n.read && isOfferPageNotification(n) && !claimedNotificationIds.current.has(n.id)
    );
    if (pending.length === 0) return;

    // Claim before awaiting. Each markAsRead write pushes a fresh snapshot,
    // which re-runs this effect while the loop below is still going; without
    // the claim the re-run would re-issue writes for the same notifications.
    pending.forEach(n => claimedNotificationIds.current.add(n.id));

    (async () => {
      console.log(`📧 Marking ${pending.length} offer notification(s) as read on TradeCenter page`);
      for (const notification of pending) {
        await markAsRead(notification.id);
      }
    })();
  }, [notifications, markAsRead]);

  const [sentOffers, setSentOffers] = useState([]);
  const [receivedOffers, setReceivedOffers] = useState([]);
  // Which list the pill bar is showing. Received leads because those are the
  // offers waiting on this user; a sent offer is waiting on someone else.
  const { savedTab, scrollSnapshot, saveView } = useReturnViewState({
    storageKey: OFFERS_VIEW_KEY,
    flowPrefixes: OFFER_FLOW_PREFIXES,
  });
  // A link can name the tab (/trade-center?tab=interests, e.g. from a
  // multi-member interest notification); it wins over the remembered tab
  // for that navigation only (hooks/useTradeCenterTab.js).
  const { activeTab, setActiveTab, instructedAtMount } = useTradeCenterTab(savedTab);
  // The restore waits until all four offer listeners have delivered once
  // (`loading` clears on the FIRST received snapshot, too early).
  const offerListsFiredRef = useRef(new Set());
  const [offerListsReady, setOfferListsReady] = useState(false);
  const markOfferListFired = (name) => {
    offerListsFiredRef.current.add(name);
    if (offerListsFiredRef.current.size === 4) setOfferListsReady(true);
  };
  const [loading, setLoading] = useState(true);
  const [deletingOfferId, setDeletingOfferId] = useState(null);
  const [usernames, setUsernames] = useState({});
  const [pedalData, setPedalData] = useState({});
  const requestedPedalIdsRef = useRef(new Set());
  const requestedUserIdsRef = useRef(new Set());
  const [currentUser, setCurrentUser] = useState(null);
  const [offersRefreshNonce, setOffersRefreshNonce] = useState(0);
  const { shippingAddress, loading: userLoading, refreshUserData, firestoreUser } = useContext(UserContext);

  // "Hey, I'm interested" signals on this user's listings (beta). Shown above
  // the offer columns and styled apart from them on purpose: an interest is not
  // an offer, and presenting it as one sets the wrong expectation for both sides.
  const interestEnabled = isListingInterestEnabled(firestoreUser);
  // First-visit tip (Jason, 2026-10-20): what the Interests tab is for.
  const { activeTip: offersTip, dismiss: dismissOffersTip, delay: offersTipDelay } = useOnboardingTips([{ id: 'offersInterests', ready: interestEnabled }]);
  const [interestSignals, setInterestSignals] = useState([]);
  // The other half of the same conversation: interests this user SENT, that the
  // owner has come back on. This is where an interest turns into an offer, so it
  // carries the pedals the owner picked and the hand-off into the offer builder.
  const [interestReplies, setInterestReplies] = useState([]);
  // True once the interests read settles (either way) — the Interests tab's
  // scroll restore waits on it, since that list loads separately.
  const [interestsLoaded, setInterestsLoaded] = useState(false);

  useEffect(() => {
    if (!interestEnabled || !currentUser) {
      setInterestSignals([]);
      setInterestReplies([]);
      // Only "loaded" when there is truly nothing to load. While auth is still
      // resolving (currentUser null on first render) the real load hasn't
      // started, and marking it loaded let a restore onto the Interests tab
      // run against an empty list.
      if (!interestEnabled) setInterestsLoaded(true);
      return;
    }
    setInterestsLoaded(false);
    let cancelled = false;
    (async () => {
      try {
        const [inboundSnap, replySnap] = await Promise.all([
          getDocs(query(
            collection(db, 'listingInterest'),
            where('listingOwnerId', '==', currentUser.uid),
            where('status', '==', 'open'),
            orderBy('createdAt', 'desc')
          )),
          getDocs(query(
            collection(db, 'listingInterest'),
            where('fromUserId', '==', currentUser.uid),
            where('status', '==', 'actioned'),
            orderBy('createdAt', 'desc')
          ))
        ]);

        // Expiry is enforced by the nightly cleanup; applied here too so the
        // hours between runs don't surface a signal that's already stale.
        const rows = inboundSnap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((r) => isInterestFresh(r.createdAt));
        // An owner can mark an interest actioned without naming pedals; only the
        // ones carrying a pick are a reply worth showing.
        const replies = replySnap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((r) =>
            Array.isArray(r.responsePedalIds) &&
            r.responsePedalIds.length > 0 &&
            isInterestFresh(r.createdAt)
          );

        // Usernames come from the public mirror, never the PII-bearing users doc.
        const otherIds = [...new Set([
          ...rows.map((r) => r.fromUserId),
          ...replies.map((r) => r.listingOwnerId)
        ].filter(Boolean))];
        const profiles = await Promise.all(
          otherIds.map((uid) => getDoc(doc(db, 'publicProfiles', uid)))
        );
        const nameByUid = {};
        profiles.forEach((p) => {
          if (p.exists()) nameByUid[p.id] = p.data().username || 'A member';
        });

        if (!cancelled) {
          setInterestSignals(rows.map((r) => ({ ...r, senderName: nameByUid[r.fromUserId] || 'A member' })));
          // The picked pedals aren't read here: the count comes off the interest
          // itself, and the offer page loads them full size.
          setInterestReplies(replies.map((r) => ({
            ...r,
            ownerName: nameByUid[r.listingOwnerId] || 'A member'
          })));
        }
      } catch (err) {
        console.error('❌ Failed to load interest signals:', err);
      } finally {
        if (!cancelled) setInterestsLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [interestEnabled, currentUser, offersRefreshNonce]);

  const [isHeaderCompact, setIsHeaderCompact] = useState(() => {
    if (typeof window === 'undefined') return false;
    if (window.innerWidth <= 768) return false;
    return window.scrollY > 400;
  });
  const [showMobileChromeBar, setShowMobileChromeBar] = useState(false);
  const [mobileChromeBarHeight, setMobileChromeBarHeight] = useState(0);
  const mobileChromeInflowRef = useRef(null);
  const mobileChromeSlotRef = useRef(null);
  const mobileChromeBarHeightRef = useRef(0);
  const navbarHeightRef = useRef(125);
  const lastScrollYRef = useRef(typeof window !== 'undefined' ? window.scrollY : 0);
  const mobileChromeHandedOffRef = useRef(false);

  // Utility to check if address is complete
  const isAddressComplete = (address) => {
    if (!address) return false;
    const required = ['name', 'street1', 'city', 'state', 'zip', 'country'];
    return required.every(field => address[field] && address[field].trim());
  };

  const handleDelete = async (offerId, offerType) => {
    const confirmDelete = window.confirm('Cancel this offer? The other member will no longer be able to accept it.');
    if (!confirmDelete) return;

    setDeletingOfferId(offerId);

    try {
      if (offerType === 'trade') {
        try {
          const clearTradeFlags = httpsCallable(functions, 'clearTradeFlags');
          await clearTradeFlags({ tradeId: offerId });
          console.log('✅ Cleared inTrade flags from pedals');
        } catch (clearError) {
          console.warn('⚠️ Could not clear trade flags (pedals may not exist or be accessible):', clearError);
        }
        
        await deleteDoc(doc(db, 'tradeOffers', offerId));
        console.log('✅ Trade deleted successfully');
      } else if (offerType === 'purchase') {
        await deleteDoc(doc(db, 'purchaseOffers', offerId));
        console.log('✅ Purchase offer deleted successfully');
      }
      
      alert('Offer cancelled.');
      
    } catch (err) {
      console.error('Error deleting offer:', err);
      alert('Failed to delete offer. Please try again.');
    } finally {
      setDeletingOfferId(null);
    }
  };



const fetchUsernames = async (offersList) => {
  const fromUserIds = offersList.map(o => o.fromUserId || o.initiatorId).filter(Boolean);
  const toUserIds = offersList.map(o => o.toUserId || o.responderId).filter(Boolean);
  const uniqueUserIds = [...new Set([...fromUserIds, ...toUserIds])];

  // Parallel, and each profile fetched once: same 5a84056 treatment as
  // fetchPedalData below, applied 2026-10-02 after the reload sat on "0"
  // placeholders for seconds. The old loop awaited each profile in turn, and
  // its "already fetched?" check read the mount render's `usernames` ({})
  // from the listener closure — so every one of the four offer listeners'
  // fires re-fetched EVERY profile, serially, again.
  const toFetch = uniqueUserIds.filter((uid) => !requestedUserIdsRef.current.has(uid));
  if (toFetch.length === 0) return;
  toFetch.forEach((uid) => requestedUserIdsRef.current.add(uid));

  const results = await Promise.all(toFetch.map(async (uid) => {
    try {
      const userDoc = await getDoc(doc(db, 'publicProfiles', uid));
      const data = userDoc.exists() ? userDoc.data() : {};
      return [uid, {
        username: data.username || 'Unknown',
        photoURL: data.photoURL || null
      }];
    } catch (err) {
      console.error('Error fetching username for', uid, err);
      requestedUserIdsRef.current.delete(uid); // let a later update retry
      return [uid, { username: 'Unknown', photoURL: null }];
    }
  }));

  const newUsernames = Object.fromEntries(results);
  setUsernames((prev) => ({ ...prev, ...newUsernames }));
};

const fetchPedalData = async (offersList) => {
  const pedalIds = new Set();
  for (const offer of offersList) {
    if (offer.offerType === 'trade') {
      const allInitiatorIds = getInitiatorPedalIds(offer);
      allInitiatorIds.forEach(id => pedalIds.add(id));
      if (offer.responderPedalId) pedalIds.add(offer.responderPedalId);
    } else {
      if (offer.targetPedalId) pedalIds.add(offer.targetPedalId);
    }
  }

  // Parallel, and each listing fetched once: the old loop awaited each read in
  // turn and its "already fetched?" check read the first render's pedalData
  // ({}) from the listener closure, so every listener fire re-read everything
  // one at a time (same fix as Offer History, 5a84056).
  const toFetch = [...pedalIds].filter((id) => !requestedPedalIdsRef.current.has(id));
  if (toFetch.length === 0) return;
  toFetch.forEach((id) => requestedPedalIdsRef.current.add(id));

  const results = await Promise.all(toFetch.map(async (pedalId) => {
    try {
      const pedalDoc = await getDoc(doc(db, 'listings', pedalId));
      if (pedalDoc.exists()) {
        const data = pedalDoc.data();
        return [pedalId, {
          make: data.make || '',
          model: data.model || '',
          image: data.images?.top || data.images?.upper || placeholderFor(data).url,
        }];
      }
      if (!pedalDoc.metadata?.fromCache) {
        // Server-confirmed deleted: resolve the placeholder to a "No img" tile.
        return [pedalId, { make: '', model: '', image: null, missing: true }];
      }
    } catch (err) {
      // fall through: unknown (offline / failed read)
    }
    requestedPedalIdsRef.current.delete(pedalId); // let a later update retry
    return null;
  }));

  const newPedalData = Object.fromEntries(results.filter(Boolean));
  if (Object.keys(newPedalData).length > 0) {
    setPedalData((prev) => ({ ...prev, ...newPedalData }));
  }
};

// While a pedal's data is still loading, hold its space with a same-size
// placeholder: rendering nothing let each band grow by a whole tile when the
// data landed, shifting everything below (and undoing a restored scroll).
const renderTilePlaceholder = () => (
  <div className="tc-tile" aria-hidden="true" style={{ backgroundColor: '#f3f4f6', borderRadius: '12px' }} />
);

const renderPedalImage = (pedalId) => {
  const pedal = pedalData[pedalId];
  if (!pedal) return renderTilePlaceholder();
  return pedal.image ? (
    <img
      src={pedal.image}
      alt={`${pedal.make} ${pedal.model}`}
      className="tc-tile"
      style={{ objectFit: 'cover', borderRadius: '12px' }}
    />
  ) : (
    <div className="tc-tile" style={{
      backgroundColor: '#f3f4f6', borderRadius: '12px',
      display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #e5e7eb'
    }}>
      <span style={{ fontSize: '0.6rem', color: '#9ca3af' }}>No img</span>
    </div>
  );
};


// Calculate when an offer will expire (48 hours from reference timestamp)
// For countered offers, prefer offerUpdatedAt (set only on offer/counter actions, not comments)
const getExpirationTime = (offer) => {
  const refTimestamp = (offer.status === 'countered')
    ? (offer.offerUpdatedAt || offer.updatedAt || offer.createdAt)
    : offer.createdAt;
  if (!refTimestamp) return null;
  
  const refDate = refTimestamp.toDate ? refTimestamp.toDate() : new Date(refTimestamp);
  const expirationDate = new Date(refDate.getTime() + (48 * 60 * 60 * 1000));
  const now = new Date();
  
  const timeLeft = expirationDate.getTime() - now.getTime();
  
  if (timeLeft <= 0) {
    return 'Expired';
  }
  
  const hoursLeft = Math.floor(timeLeft / (1000 * 60 * 60));
  const minutesLeft = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
  
  if (hoursLeft > 24) {
    const daysLeft = Math.floor(hoursLeft / 24);
    const remainingHours = hoursLeft % 24;
    return `${daysLeft}d ${remainingHours}h remaining`;
  } else if (hoursLeft > 0) {
    return `${hoursLeft}h ${minutesLeft}m remaining`;
  } else {
    return `${minutesLeft}m remaining`;
  }
};


        useEffect(() => {
  let unsub1 = null;
  let unsub2 = null;
  let unsubPurchase1 = null;
  let unsubPurchase2 = null;

  const filterAndSortOffers = (offers) => {
    const active = offers.filter(o => {
      if (o.status === 'expired') return false;
      const refTs = (o.status === 'countered' && o.updatedAt) ? o.updatedAt : o.createdAt;
      if (refTs) {
        const refDate = refTs.toDate ? refTs.toDate() : new Date(refTs);
        const expirationDate = new Date(refDate.getTime() + (48 * 60 * 60 * 1000));
        if (new Date() > expirationDate) return false;
      }
      return true;
    });
    active.sort((a, b) => {
      const aDate = a.updatedAt?.toDate?.() || a.createdAt?.toDate?.() || 0;
      const bDate = b.updatedAt?.toDate?.() || b.createdAt?.toDate?.() || 0;
      return bDate - aDate;
    });
    return active;
  };

  let sentTradeOffers = [];
  let sentPurchaseOffers = [];
  let receivedTradeOffers = [];
  let receivedPurchaseOffers = [];

  const updateSentOffers = () => {
    const all = [...sentTradeOffers, ...sentPurchaseOffers];
    setSentOffers(filterAndSortOffers(all));
    fetchUsernames(all);
    fetchPedalData(all);
  };

  const updateReceivedOffers = () => {
    const all = [...receivedTradeOffers, ...receivedPurchaseOffers];
    setReceivedOffers(filterAndSortOffers(all));
    fetchUsernames(all);
    fetchPedalData(all);
    setLoading(false);
  };

  const unsubscribeAuth = auth.onAuthStateChanged((user) => {
    // An auth change (sign-out / account switch) must drop the previous
    // user's listeners, not just overwrite the handles and orphan them.
    if (unsub1) unsub1();
    if (unsub2) unsub2();
    if (unsubPurchase1) unsubPurchase1();
    if (unsubPurchase2) unsubPurchase2();
    unsub1 = unsub2 = unsubPurchase1 = unsubPurchase2 = null;
    setCurrentUser(user);
    if (!user) {
      setLoading(false);
      return;
    }

    const initiatorQuery = query(
      collection(db, 'tradeOffers'),
      where('fromUserId', '==', user.uid),
      where('status', 'in', ['pending', 'countered'])
    );
    const responderQuery = query(
      collection(db, 'tradeOffers'),
      where('toUserId', '==', user.uid),
      where('status', 'in', ['pending', 'countered'])
    );
    const purchaseInitiatorQuery = query(
      collection(db, 'purchaseOffers'),
      where('initiatorId', '==', user.uid),
      where('status', 'in', ['pending', 'countered'])
    );
    const purchaseResponderQuery = query(
      collection(db, 'purchaseOffers'),
      where('responderId', '==', user.uid),
      where('status', 'in', ['pending', 'countered'])
    );

    // 4 parallel top-level listeners (no nesting)
    unsub1 = onSnapshot(initiatorQuery, (snapshot) => {
      sentTradeOffers = [];
      snapshot.forEach((d) => {
        sentTradeOffers.push({ id: d.id, ...d.data(), offerType: 'trade' });
      });
      updateSentOffers();
      markOfferListFired('sentTrade');
    }, (err) => {
      // A failed list still counts as "arrived" so the restore can't be
      // stranded waiting on it.
      console.error('Offers listener failed (sentTrade):', err);
      markOfferListFired('sentTrade');
    });

    unsubPurchase1 = onSnapshot(purchaseInitiatorQuery, (snapshot) => {
      sentPurchaseOffers = [];
      snapshot.forEach((d) => {
        sentPurchaseOffers.push({ id: d.id, ...d.data(), offerType: 'purchase' });
      });
      updateSentOffers();
      markOfferListFired('sentPurchase');
    }, (err) => {
      // A failed list still counts as "arrived" so the restore can't be
      // stranded waiting on it.
      console.error('Offers listener failed (sentPurchase):', err);
      markOfferListFired('sentPurchase');
    });

    unsub2 = onSnapshot(responderQuery, (snapshot) => {
      receivedTradeOffers = [];
      snapshot.forEach((d) => {
        receivedTradeOffers.push({ id: d.id, ...d.data(), offerType: 'trade' });
      });
      updateReceivedOffers();
      markOfferListFired('receivedTrade');
    }, (err) => {
      // A failed list still counts as "arrived" so the restore can't be
      // stranded waiting on it.
      console.error('Offers listener failed (receivedTrade):', err);
      markOfferListFired('receivedTrade');
    });

    unsubPurchase2 = onSnapshot(purchaseResponderQuery, (snapshot) => {
      receivedPurchaseOffers = [];
      snapshot.forEach((d) => {
        receivedPurchaseOffers.push({ id: d.id, ...d.data(), offerType: 'purchase' });
      });
      updateReceivedOffers();
      markOfferListFired('receivedPurchase');
    }, (err) => {
      // A failed list still counts as "arrived" so the restore can't be
      // stranded waiting on it.
      console.error('Offers listener failed (receivedPurchase):', err);
      markOfferListFired('receivedPurchase');
    });
  });

  return () => {
    unsubscribeAuth();
    if (unsub1) unsub1();
    if (unsub2) unsub2();
    if (unsubPurchase1) unsubPurchase1();
    if (unsubPurchase2) unsubPurchase2();
  };
}, [offersRefreshNonce]);

  const visibleTab = visibleTradeCenterTab(activeTab, interestEnabled);

  const refreshOffers = useCallback(async () => {
    setUsernames({});
    setPedalData({});
    // pedalData/usernames were just emptied, so every listing and profile
    // must be fetchable again — otherwise the refreshed bands keep grey
    // placeholder tiles and 'Unknown' names forever.
    requestedPedalIdsRef.current = new Set();
    requestedUserIdsRef.current = new Set();
    setLoading(true);
    setOffersRefreshNonce((n) => n + 1);
    if (refreshUserData) {
      await refreshUserData();
    }
  }, [refreshUserData]);

  useRegisterPullToRefresh(refreshOffers);

  // The SPA has no router scroll restoration, so arriving here inherits the
  // previous page's scroll offset — submitting an offer from the bottom of the
  // long PurchaseOffer/TradeOfferForm pages landed the user mid-page here
  // (found during Android testing, 2026-08-20). scrollPageToTop's cleanup is
  // the effect cleanup so its retry window can't fight a fast unmount.
  // Always runs, even when returning with a saved position: if the restore
  // below never gets its lists, the user is left at the top, not at whatever
  // offset the previous page carried over. The restore cancels this loop at
  // the moment it starts.
  const cancelScrollTopRef = useRef(null);
  useLayoutEffect(() => {
    const cancel = scrollPageToTop({ blur: false });
    cancelScrollTopRef.current = cancel;
    return cancel;
  }, []);

  // The tab actually on screen: an account without Interests that was sent
  // there falls back to Received, and that's what should be remembered.
  const saveViewState = () => saveView(visibleTab);

  // Interests load separately, so a restore onto that tab also waits for them.
  // A link that named the tab skips the remembered scroll position: it was
  // saved for the remembered tab, not the one the link opened.
  const restoreSnapshot = instructedAtMount ? null : scrollSnapshot;
  useRestoreScroll(
    restoreSnapshot,
    offerListsReady && (restoreSnapshot?.tab !== 'interests' || interestsLoaded),
    { onStart: () => cancelScrollTopRef.current?.() }
  );

  useLayoutEffect(() => {
    const readNavbarHeight = () => {
      const v = getComputedStyle(document.documentElement).getPropertyValue('--navbar-height').trim();
      navbarHeightRef.current = v ? parseFloat(v) : 125;
    };
    readNavbarHeight();
    window.addEventListener('resize', readNavbarHeight);
    return () => window.removeEventListener('resize', readNavbarHeight);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY;
      const isMobile = window.innerWidth <= 768;
      if (isMobile) {
        const lastY = lastScrollYRef.current;
        const delta = y - lastY;
        const chromeSlotBottom = navbarHeightRef.current + mobileChromeBarHeightRef.current;
        const pastChromeSlot = y > chromeSlotBottom;
        const slotTop = mobileChromeSlotRef.current?.getBoundingClientRect().top ?? -1;

        if (y > chromeSlotBottom + 30) {
          mobileChromeHandedOffRef.current = false;
        }

        if (slotTop >= 0 || y <= 10) {
          setShowMobileChromeBar(false);
          if (slotTop >= 0) mobileChromeHandedOffRef.current = true;
        } else if (delta > 5) {
          setShowMobileChromeBar(false);
        } else if (delta < -5 && pastChromeSlot && !mobileChromeHandedOffRef.current) {
          setShowMobileChromeBar(true);
        }
        lastScrollYRef.current = y;
      } else {
        setIsHeaderCompact(y > 400);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useLayoutEffect(() => {
    const el = mobileChromeInflowRef.current;
    if (!el) return;
    const measure = () => {
      const h = el.offsetHeight;
      if (h > 0) {
        mobileChromeBarHeightRef.current = h;
        setMobileChromeBarHeight(h);
      }
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [currentUser]);

  const offerHistoryBtnStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '0.5rem 1rem',
    borderRadius: '9999px',
    border: '1px solid #C89F3C',
    backgroundColor: 'white',
    color: '#374151',
    fontFamily: 'Nunito, sans-serif',
    fontWeight: 700,
    fontSize: '0.875rem',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  };

  // One renderer for both directions. Sent and received used to be two
  // near-identical 180-line JSX blocks sitting side by side in a `1fr 1fr`
  // grid, and that grid is why every label in them had shrunk to 0.55rem —
  // there was only half a page to fit a card into. Moving the two lists behind
  // tabs handed the card the full width back, so the copies collapsed into
  // this one function and the type went back up to a readable size.
  const renderOfferBand = (offer, direction) => {
    const isSent = direction === 'sent';
    const otherUid = offer.offerType === 'purchase'
      ? (isSent ? offer.responderId : offer.initiatorId)
      : (isSent ? offer.toUserId : offer.fromUserId);
    const userData = usernames[otherUid];
    const username = typeof userData === 'object' ? userData?.username : (userData || 'Unknown');
    const photoURL = typeof userData === 'object' ? userData?.photoURL : null;
    const initials = (username || '??').slice(0, 2).toUpperCase();
    const dateStr = offer.createdAt?.toDate
      ? `${offer.createdAt.toDate().toLocaleDateString()} · ${offer.createdAt.toDate().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
      : 'N/A';
    const cashAmount = offer.offerType === 'purchase'
      ? (offer.counterAmount ?? offer.offerAmount ?? 0)
      : (offer.counterAmount ?? offer.cashAmount ?? 0);
    const actionEntries = offer.history?.filter(e => e.type === 'offer' || e.type === 'response') || [];
    const lastActionFrom = actionEntries.length > 0 ? actionEntries[actionEntries.length - 1].from : null;
    const youSentLast = lastActionFrom === auth.currentUser?.uid;

    // The footer hint answers one question — is the ball in my court? — so it
    // is derived from who moved last, not from the status, which stays
    // 'countered' through a counter-counter while the direction flips.
    let statusLabel, statusColor, hint, hintColor;
    if (offer.status === 'countered') {
      statusLabel = 'Countered';
      statusColor = '#b45309';
      hint = youSentLast ? 'Waiting for response' : 'Counter received — needs response';
      hintColor = youSentLast ? '#b8912a' : '#dc2626';
    } else {
      statusLabel = 'Active';
      statusColor = '#16a34a';
      hint = isSent ? 'Waiting for response' : 'Needs response';
      hintColor = isSent ? '#b8912a' : '#dc2626';
    }
    const expiration = getExpirationTime(offer) || 'Unknown';
    const detailsPath = offer.offerType === 'purchase'
      ? `/purchase-offer-details/${offer.id}`
      : `/offer/${offer.id}`;

    // "Offer type / Amount" said nothing about which way the money moves, so
    // a $120 trade looked identical whether you were paying it or collecting
    // it. Trades carry cashFlow.{payer,receiver}, written on the original offer
    // and rewritten on every counter; a straight trade has no cashFlow at all.
    // Purchases have no such field because the direction is fixed — the
    // initiator is the buyer, so money always runs initiator → responder.
    const myUid = auth.currentUser?.uid;
    let cashPayerIsMe;
    if (offer.offerType === 'purchase') {
      cashPayerIsMe = offer.initiatorId === myUid;
    } else if (offer.cashFlow?.payer) {
      cashPayerIsMe = offer.cashFlow.payer === myUid;
    } else {
      // Legacy trade docs predate cashFlow. `type` has always been set:
      // 'offer' means the initiator adds cash, 'ask' means the responder does.
      const legacyPayer = offer.type === 'ask' ? offer.toUserId : offer.fromUserId;
      cashPayerIsMe = legacyPayer === myUid;
    }
    const hasCash = Number(cashAmount) > 0;
    // Whoever moved LAST owns the terms on the table. Keying this off isSent
    // instead — who opened the negotiation — narrated every counter as though
    // it were still your proposal: ask them for $30, have them counter
    // offering $10, and the band read "You're asking for $10" when it was
    // their offer of $10. lastActionFrom is written by the counter path in
    // OfferPage as {type:'response', from: <counterer>}; fall back to the
    // offer's origin for legacy docs that carry no history.
    const proposerIsMe = lastActionFrom ? youSentLast : isSent;
    const cashPhrase = (proposerIsMe ? "You're " : "They're ")
      + (proposerIsMe === cashPayerIsMe ? 'offering' : 'asking for');
    const dealLabel = offer.offerType === 'purchase'
      ? 'Purchase'
      : (hasCash ? 'Trade + cash' : 'Straight trade');

    const initiatorIds = getInitiatorPedalIds(offer);
    const isMulti = initiatorIds.length > 1;
    const initiatorLabel = `${isSent ? 'Your' : 'Their'} pedal${isMulti ? `s (${initiatorIds.length})` : ''}`;

    return (
      <div key={offer.id} className="tc-band" data-restore-anchor={offer.id}>
        {/* Who and when, plus the status the whole band is about */}
        <div className="tc-band-head">
          <div className="tc-band-avatar">
            {photoURL
              ? <img src={photoURL} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : initials}
          </div>
          <div className="tc-band-who">
            <div className="tc-band-name">
              <span className="tc-band-from">{isSent ? 'Sent to' : 'Offer from'}</span>
              {username}
            </div>
            <div className="tc-band-date">{dateStr}</div>
          </div>
          <span className="tc-band-status" style={{ color: statusColor }}>{statusLabel}</span>
        </div>

        {/* What is on the table, and for how much */}
        <div className="tc-band-body">
          {/* data-swipe-back-opt-out: same contract as Offer History's strip.
              /trade-center stays route-opted-out (bottom-tab root, iOS
              convention: tab roots don't swipe back), so this is inert today
              and protects the strip if that route decision ever changes. */}
          <div className="tc-band-pedals" data-swipe-back-opt-out="true">
            {offer.offerType === 'trade' ? (
              <>
                <div className="tc-band-side">
                  {isMulti ? (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      {initiatorIds.map((id, idx) => {
                        const pedal = pedalData[id];
                        if (!pedal) {
                          return (
                            <div key={id} className={idx === 0 ? '' : 'tc-tile-stacked'} style={{ zIndex: initiatorIds.length - idx, position: 'relative' }}>
                              {renderTilePlaceholder()}
                            </div>
                          );
                        }
                        return (
                          <div key={id} className={idx === 0 ? '' : 'tc-tile-stacked'} style={{ zIndex: initiatorIds.length - idx, position: 'relative' }}>
                            {pedal.image ? (
                              <img
                                src={pedal.image}
                                alt={`${pedal.make} ${pedal.model}`}
                                className="tc-tile"
                                style={{ objectFit: 'cover', borderRadius: '12px', border: '3px solid white', boxShadow: '0 1px 3px rgba(0,0,0,0.12)' }}
                              />
                            ) : (
                              <div className="tc-tile" style={{ backgroundColor: '#f3f4f6', borderRadius: '12px', border: '3px solid white', boxShadow: '0 1px 3px rgba(0,0,0,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <span style={{ fontSize: '0.6rem', color: '#9ca3af' }}>No img</span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    renderPedalImage(initiatorIds[0])
                  )}
                  <div className="tc-band-side-label">{initiatorLabel}</div>
                </div>
                <span className="tc-band-arrow" aria-hidden="true">⇄</span>
                <div className="tc-band-side">
                  {renderPedalImage(offer.responderPedalId)}
                  <div className="tc-band-side-label">{isSent ? 'Their pedal' : 'Your pedal'}</div>
                </div>
              </>
            ) : (
              <div className="tc-band-side">
                {renderPedalImage(offer.targetPedalId)}
                <div className="tc-band-side-label">{isSent ? 'Their pedal' : 'Your pedal'}</div>
              </div>
            )}
          </div>

          <div className="tc-band-figures">
            <div className="tc-band-figure">
              <span className="tc-band-figure-label">Offer type</span>
              <span className="tc-band-figure-value">{dealLabel}</span>
            </div>
            {hasCash && (
              <div className="tc-band-figure">
                <span className="tc-band-figure-label">Cash</span>
                <span className="tc-band-figure-value tc-band-amount">
                  <span className="tc-band-amount-who tc-wide-only">{cashPhrase}</span>
                  <span>${cashAmount}</span>
                  <span className="tc-band-amount-who tc-narrow-only">{cashPayerIsMe ? 'from you' : 'to you'}</span>
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="tc-band-actions">
          <Link to={detailsPath} className="tc-band-cta" onClick={saveViewState}>View offer details</Link>
          {isSent && offer.status === 'pending' && (
            <button
              type="button"
              onClick={() => handleDelete(offer.id, offer.offerType)}
              disabled={deletingOfferId === offer.id}
              className="tc-band-secondary"
            >
              {deletingOfferId === offer.id ? 'Cancelling...' : 'Cancel offer'}
            </button>
          )}
        </div>

        {/* The one line that says whether this needs you today: the nudge on
            the left, the clock on the right. */}
        <div className="tc-band-foot">
          <span className="tc-band-foot-left">
            <span className="tc-band-hint" style={{ color: hintColor }}>{hint}</span>
            <SeenBadge offer={offer} />
          </span>
          <span className="tc-band-expiry">{expiration}</span>
        </div>
      </div>
    );
  };

  const renderMobileChromeBar = () => (
    <div className="tc-compact-bar-inner">
      <span className="tc-compact-title">Offers</span>
      <span className="tc-compact-spacer" aria-hidden="true" />
      {currentUser && (
        <Link to="/offer-history" className="tc-history-btn" style={offerHistoryBtnStyle} onClick={saveViewState}>
          Offer History
        </Link>
      )}
    </div>
  );

  if (loading) return <div className="p-4">Loading your active offers...</div>;

  // Only check shipping address if user is authenticated
  if (auth.currentUser && !userLoading && !isAddressComplete(shippingAddress)) {
    return (
      <div className="p-4 bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 rounded">
        <h2 className="text-xl font-semibold mb-2">Shipping address required</h2>
        <p className="mb-2">You must complete your shipping address before accessing trade actions.</p>
        <a href="/profile" className="text-blue-700 underline">Go to Profile</a>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'transparent', minHeight: 'auto', display: 'flex', flexDirection: 'column' }}>
      {offersTip && <OnboardingTip key={offersTip} tipId={offersTip} onGotIt={dismissOffersTip} delay={offersTipDelay} />}
      {/* Yellow background section */}
      <div 
        className="trade-center-page" 
        style={{ 
          backgroundColor: '#F7F3EB', 
          flex: 1, 
          flexDirection: 'column',
          padding: '0 1rem',
          display: 'flex',
        }}
      >
      <div className="trade-center-page-content" style={{ paddingTop: '2rem', paddingBottom: '2rem', flex: 1 }}>
        <div className={`tc-sticky-header ${isHeaderCompact ? 'is-compact' : ''}`}>
          <style>{`
            .tc-compact-bar-inner {
              max-width: 1100px;
              margin: 0 auto;
              width: 100%;
              box-sizing: border-box;
              display: flex;
              justify-content: flex-start;
              align-items: center;
              gap: 12px;
            }
            .tc-compact-title {
              font-family: Nunito, sans-serif;
              font-weight: 700;
              font-size: 1.125rem;
              color: #111827;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              min-width: 0;
              flex: 0 0 auto;
              text-align: left;
            }
            .tc-compact-spacer {
              flex: 1 1 auto;
              min-width: 0;
            }
            .tc-sticky-header .tc-history-btn {
              flex-shrink: 0;
            }
            .tc-sticky-header .tc-header-expanded {
              display: block;
            }
            .tc-sticky-header .tc-mobile-chrome-slot {
              display: none;
            }
            .tc-sticky-header .tc-compact-bar-mobile-fixed {
              display: none;
            }
            @media (min-width: 769px) {
              .tc-sticky-header .tc-compact-bar-mobile-fixed {
                display: none !important;
              }
              .tc-sticky-header .tc-compact-bar {
                position: fixed;
                top: 0;
                left: 0;
                right: 0;
                z-index: 100;
                padding: 0.6rem 1rem 0.7rem 1rem;
                background-color: #F7F3EB;
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
                box-sizing: border-box;
                opacity: 0;
                pointer-events: none;
                transition: opacity 0.2s ease;
              }
              .tc-sticky-header.is-compact .tc-compact-bar {
                opacity: 1;
                pointer-events: auto;
              }
              .tc-sticky-header .tc-history-btn {
                padding: 0.6rem 1.4rem !important;
              }
            }
            @media (max-width: 768px) {
              .trade-center-page-content {
                padding-top: 0 !important;
              }
              .tc-sticky-header .tc-header-expanded {
                display: none !important;
              }
              .tc-sticky-header .tc-mobile-chrome-slot {
                display: block !important;
                position: relative;
                flex-shrink: 0;
                background-color: #F7F3EB;
                overflow: hidden;
                margin-left: -0.75rem;
                margin-right: -0.75rem;
                width: calc(100% + 1.5rem);
              }
              .tc-sticky-header .tc-compact-bar-inflow {
                display: block;
                padding: 0.6rem 1rem 1.5rem 1rem;
                background-color: #F7F3EB;
                box-sizing: border-box;
              }
              .tc-sticky-header .tc-compact-bar-inflow.is-suppressed {
                visibility: hidden;
                pointer-events: none;
              }
              .tc-sticky-header .tc-compact-bar-desktop {
                display: none !important;
              }
              .tc-sticky-header .tc-compact-bar-mobile-fixed {
                display: block;
              }
              .tc-sticky-header .tc-compact-bar {
                display: none;
                position: fixed;
                top: 0;
                left: 0;
                right: 0;
                z-index: 100;
                padding: 0.6rem 1rem 0.85rem 1rem;
                background-color: #F7F3EB;
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
                box-sizing: border-box;
                pointer-events: none;
                transform: translateY(-100%);
                transition: transform 0.2s ease;
              }
              .tc-sticky-header .tc-compact-bar.is-mobile-visible {
                display: block;
                transform: translateY(0);
                pointer-events: auto;
              }
              .tc-sticky-header .tc-compact-title {
                font-size: calc(1rem + 1pt) !important;
                color: #5A9ECB !important;
              }
              .tc-sticky-header .tc-history-btn {
                padding: 0.5rem 1rem !important;
                font-size: 0.875rem !important;
              }
            }
          `}</style>

          <div
            ref={mobileChromeSlotRef}
            className="tc-mobile-chrome-slot"
            style={mobileChromeBarHeight > 0 ? { height: mobileChromeBarHeight, minHeight: mobileChromeBarHeight } : undefined}
          >
            <div
              ref={mobileChromeInflowRef}
              className={['tc-compact-bar-inflow', showMobileChromeBar && 'is-suppressed'].filter(Boolean).join(' ')}
            >
              {renderMobileChromeBar()}
            </div>
          </div>

          <div
            className={[
              'tc-compact-bar',
              showMobileChromeBar && 'is-mobile-visible',
            ].filter(Boolean).join(' ')}
          >
            <div className="tc-compact-bar-mobile-fixed">
              {renderMobileChromeBar()}
            </div>
            <div className="tc-compact-bar-desktop">
              {renderMobileChromeBar()}
            </div>
          </div>

          <div className="tc-header-expanded">
            <Link 
              to="/trading"
              className="trade-center-header-back-link"
              style={{
                color: '#5A9ECB',
                textDecoration: 'none',
                alignItems: 'center',
                marginBottom: '1rem',
                fontSize: '1.125rem'
              }}
            >
              ← Back to Trading
            </Link>
            <div className="trade-center-header-expanded-inner">
              <div className="trade-center-header-copy">
                <h1 className="font-bold text-gray-900" style={{ fontSize: '2.25rem', fontWeight: '700', letterSpacing: '-0.025em' }}>
                  Offers
                </h1>
              </div>
              {currentUser && (
                <div className="trade-center-header-links">
                  <Link to="/offer-history" className="tc-history-btn" style={offerHistoryBtnStyle} onClick={saveViewState}>
                    Offer History
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>

      <style jsx>{`
        @media (min-width: 769px) {
          .trade-center-page {
            padding-left: 0 !important;
            padding-right: 0 !important;
          }
          .trade-center-header-back-link {
            max-width: 1100px;
            width: 100%;
            margin-left: auto;
            margin-right: auto;
            padding-left: 2rem;
            padding-right: 2rem;
            box-sizing: border-box;
            display: flex !important;
            align-items: center;
          }
          .trade-center-header-expanded-inner {
            max-width: 1100px;
            width: 100%;
            margin-left: auto;
            margin-right: auto;
            padding-left: 2rem;
            padding-right: 2rem;
            box-sizing: border-box;
            display: flex;
            flex-wrap: wrap;
            align-items: flex-start;
            gap: 0.5rem 5rem;
            margin-bottom: 1.5rem;
          }
          .trade-center-header-copy {
            flex: 0 1 auto;
            min-width: 0;
          }
          .trade-center-header-links {
            flex: 0 0 auto;
            margin-left: auto;
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            gap: 0.5rem;
            align-self: flex-end;
          }
          .trade-center-header-expanded-inner h1 {
            text-align: left !important;
            margin: 0 !important;
          }
          .tc-tabs,
          .tc-panel {
            max-width: 1100px !important;
            padding: 0 2rem !important;
            box-sizing: border-box !important;
          }
        }
        @media (max-width: 768px) {
          .trade-center-page {
            padding: 0 0.75rem !important;
          }
          .interest-signal-thumb {
            width: 76px !important;
            height: 76px !important;
          }
          /* The sentence wraps to several lines at this width, so the date and
             link need a little air to read as a separate row. */
          .interest-signal-meta {
            margin-top: 8px !important;
          }
        }
      `}</style>
      
      {!currentUser && (
        <div style={{ margin: '2rem auto', maxWidth: '600px' }}>
          <div className='mb-4 p-4 border border-blue-300 rounded-lg bg-blue-50 text-center'>
            <p className='text-blue-800 text-lg font-semibold mb-2'>Create an account to start trading!</p>
            <p className='text-blue-700 text-sm mb-3'>
              Join thousands of musicians trading pedals safely and affordably.
            </p>
            <Link to='/signup' className='inline-block px-4 py-2 text-sm font-medium' style={{ marginRight: '12px', color: '#5A9ECB', textDecoration: 'none' }}>
              Sign Up
            </Link>
            <span className='text-blue-700 text-sm font-medium' style={{ margin: '0 8px' }}>or</span>
            <Link to='/login' className='inline-block px-4 py-2 text-sm font-medium' style={{ marginLeft: '12px', color: '#5A9ECB', textDecoration: 'none' }}>
              Log In
            </Link>
          </div>
        </div>
      )}

      <style>{`
        /* Three fixed tabs sharing the width evenly. Nothing scrolls: the
           only pill that ever pushed this over a phone's width was a duplicate
           of the History button in the sticky bar above. */
        .tc-tabs {
          max-width: 1100px;
          margin: 0 auto 1.25rem;
          width: 100%;
          box-sizing: border-box;
          display: flex;
          align-items: stretch;
          gap: 8px;
        }
        .tc-tab {
          /* Basis auto rather than 0: each pill starts at its label's natural
             width and the leftover space is split evenly on top, so "Sent"
             stays the narrow one instead of every pill being forced to the
             same width and leaving it looking hollow. */
          flex: 1 1 auto;
          justify-content: center;
          min-width: 0;
          display: inline-flex;
          align-items: center;
          gap: 7px;
          padding: 0.55rem 1.1rem;
          border-radius: 9999px;
          border: 1px solid #e5e0d4;
          background-color: white;
          color: #6b7280;
          font-family: Nunito, sans-serif;
          font-weight: 700;
          font-size: 0.95rem;
          text-decoration: none;
          white-space: nowrap;
          cursor: pointer;
          transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease;
        }
        .tc-tab:hover { border-color: #C89F3C; }
        .tc-tab.is-active {
          background-color: #C89F3C;
          border-color: #C89F3C;
          color: white;
        }
        .tc-tab-count {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 22px;
          height: 22px;
          padding: 0 6px;
          border-radius: 9999px;
          background-color: #ece4d2;
          color: #3f3f46;
          font-size: 0.78rem;
          font-weight: 800;
        }
        /* Translucent white on gold left the digit barely visible; a solid
           white chip with the gold read back out of it is legible in both
           states without changing the pill's shape. */
        .tc-tab.is-active .tc-tab-count {
          background-color: #ffffff;
          color: #9a7526;
        }

        .tc-panel { max-width: 1100px; margin: 0 auto; width: 100%; box-sizing: border-box; }
        .tc-list { display: flex; flex-direction: column; gap: 12px; }
        .tc-empty {
          font-size: 0.95rem; color: #9ca3af; text-align: center;
          padding: 3rem 1rem; margin: 0;
        }
        .tc-section-title { font-size: 1.05rem; font-weight: 700; color: #111827; margin: 0 0 4px; }
        .tc-section-count { color: #9ca3af; font-weight: 500; }
        .tc-section-sub { font-size: 0.85rem; color: #6b7280; margin: 0 0 12px; }

        .tc-band {
          background-color: white;
          border-radius: 14px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
          overflow: hidden;
          text-align: left;
        }
        .tc-band-head {
          display: flex; align-items: center; gap: 10px;
          padding: 14px 18px;
          border-bottom: 1px solid #f3f0e8;
        }
        .tc-band-avatar {
          width: 38px; height: 38px; border-radius: 50%; overflow: hidden; flex-shrink: 0;
          background-color: #fef3c7; display: flex; align-items: center; justify-content: center;
          font-size: 0.75rem; font-weight: 700; color: #92400e;
        }
        .tc-band-who { flex: 1; min-width: 0; }
        .tc-band-name {
          font-weight: 700; font-size: 1rem; color: #111827;
          overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
        }
        .tc-band-from { font-weight: 500; color: #6b7280; margin-right: 6px; }
        .tc-band-date { font-size: 0.75rem; color: #9ca3af; }
        .tc-band-status { flex-shrink: 0; font-size: 0.95rem; font-weight: 700; white-space: nowrap; }

        .tc-band-body { display: flex; align-items: center; gap: 24px; padding: 16px 18px; }
        .tc-band-pedals {
          display: flex; align-items: center; gap: 22px; flex-shrink: 0;
          --tc-tile: 132px;
        }
        .tc-tile { width: var(--tc-tile); height: var(--tc-tile); flex-shrink: 0; }
        /* Overlap is a fraction of the tile so the fan-out looks the same at
           every size. */
        .tc-tile-stacked { margin-left: calc(var(--tc-tile) * -0.3); }
        .tc-band-side { text-align: center; }
        .tc-band-side { flex-shrink: 0; }
        .tc-band-side-label { font-size: 0.8rem; font-weight: 600; color: #6b7280; margin-top: 10px; }
        .tc-band-arrow { font-size: 30px; color: #9ca3af; flex-shrink: 0; }

        .tc-band-figures {
          display: flex; flex-direction: column; gap: 14px;
          align-self: stretch; align-items: flex-start; justify-content: center;
          padding-left: 28px; border-left: 1px solid #f0ece2;
        }
        .tc-band-figure { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
        /* The label was a faint 0.68rem grey under a 1.3rem near-black value —
           too big a jump in both size and weight to read as one unit. Closing
           the size gap and darkening the label pairs them. */
        .tc-band-figure-label {
          white-space: nowrap;
          font-size: 0.72rem; font-weight: 700; color: #6b7280;
          letter-spacing: 0.06em; text-transform: uppercase;
        }
        .tc-band-figure-value { font-size: 1.05rem; font-weight: 700; color: #1f2937; white-space: nowrap; }
        .tc-band-amount {
          font-size: 1.05rem; display: flex; align-items: baseline; gap: 6px;
          flex-wrap: nowrap; white-space: nowrap;
        }
        .tc-band-amount-who { font-size: inherit; font-weight: inherit; color: inherit; }
        .tc-narrow-only { display: none; }

        .tc-band-actions { display: flex; gap: 10px; padding: 0 18px 14px; }
        .tc-band-cta {
          flex: 2; display: block; text-align: center; text-decoration: none;
          background-color: #5A9ECB; color: white; border-radius: 9999px;
          padding: 12px 20px; font-family: Nunito, sans-serif;
          font-weight: 700; font-size: 0.95rem;
          transition: opacity 0.15s ease;
        }
        .tc-band-cta:hover { opacity: 0.9; }
        .tc-band-secondary {
          flex: 1; background-color: #E5E7EB; color: #374151; border: none;
          border-radius: 9999px; padding: 12px 20px;
          font-family: Nunito, sans-serif; font-weight: 700; font-size: 0.95rem;
          cursor: pointer;
        }
        .tc-band-secondary:disabled { opacity: 0.6; cursor: not-allowed; }

        .tc-band-foot {
          display: flex; align-items: flex-start; justify-content: space-between; gap: 12px;
          padding: 14px 18px;
          background-color: #faf8f3;
          border-top: 1px solid #f3f0e8;
        }
        .tc-band-hint { font-size: 0.95rem; font-weight: 700; }
        .tc-band-foot-left {
          display: flex; flex-direction: column; align-items: flex-start;
          gap: 2px; min-width: 0;
        }
        .tc-band-foot-left .offer-seen-badge { font-size: 0.9rem; }
        .tc-band-expiry { font-size: 0.95rem; color: #6b7280; white-space: nowrap; }

        @media (max-width: 768px) {
          .tc-band-body { flex-direction: column; align-items: stretch; gap: 16px; }
          /* Four stacked tiles plus the other side plus the arrow is wider
             than a phone even after stepping the tile down, and shrinking far
             enough to fit that case would punish the common one- and two-pedal
             offers. So the row scrolls on its own instead — the page body must
             never scroll sideways. */
          .tc-band-pedals {
            /* "safe" keeps the pair centred normally but falls back to
               flex-start when it overflows, so a wide stack scrolls from its
               first tile instead of having that tile clipped off. */
            justify-content: center; justify-content: safe center;
            gap: 14px; --tc-tile: 108px;
            overflow-x: auto; -webkit-overflow-scrolling: touch;
            scrollbar-width: none; padding-bottom: 2px;
          }
          .tc-band-pedals::-webkit-scrollbar { display: none; }
          .tc-band-arrow { font-size: 24px; }
          .tc-band-figures {
            flex-direction: row; justify-content: space-between; gap: 12px;
            align-self: auto; padding-left: 0; border-left: none;
          }
          .tc-band-figure { gap: 2px; }
          .tc-band-figure-label { white-space: normal; }
          .tc-band-figure-value { white-space: normal; }
          .tc-band-amount { font-size: 1.15rem; flex-wrap: wrap; white-space: normal; }
          .tc-band-amount-who { font-size: 0.85rem; font-weight: 600; }
          .tc-band-actions { flex-direction: column; }
          .tc-band-secondary { width: 100%; }
          .tc-wide-only { display: none; }
          .tc-narrow-only { display: inline; }
          .tc-band-foot { flex-wrap: wrap; row-gap: 4px; padding: 10px 18px; }
          .tc-band-hint { font-size: 0.8rem; }
          .tc-band-foot-left .offer-seen-badge { font-size: 0.8rem; }
          .tc-band-expiry { font-size: 0.8rem; margin-left: auto; }
        }
      `}</style>

      {currentUser && (
        /* Horizontal pill bar in place of the old two-column split. The columns
           forced both lists into half the page width, which is what drove the
           type down to 0.55rem chips; one list at a time reads at full size.
           History stays a link rather than a tab — it is a separate page with
           its own queries over terminal-status offers. */
        <div className="tc-tabs" role="tablist" aria-label="Offer lists">
          <button
            type="button"
            role="tab"
            aria-selected={visibleTab === 'received'}
            className={`tc-tab ${visibleTab === 'received' ? 'is-active' : ''}`}
            onClick={() => setActiveTab('received')}
          >
            Received <span className="tc-tab-count">{offerListsReady ? receivedOffers.length : '…'}</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={visibleTab === 'sent'}
            className={`tc-tab ${visibleTab === 'sent' ? 'is-active' : ''}`}
            onClick={() => setActiveTab('sent')}
          >
            Sent <span className="tc-tab-count">{offerListsReady ? sentOffers.length : '…'}</span>
          </button>
          {interestEnabled && (
            <button
              type="button"
              role="tab"
              aria-selected={visibleTab === 'interests'}
              className={`tc-tab ${visibleTab === 'interests' ? 'is-active' : ''}`}
              data-tip-anchor="interests-tab"
              onClick={() => setActiveTab('interests')}
            >
              {/* Counts stay '…' until their source has actually answered:
                  asserting "0" while the fetch is in flight is the same false
                  empty-state class the 2026-09-29 recovery work removed. */}
              Interests <span className="tc-tab-count">{interestsLoaded ? interestSignals.length + interestReplies.length : '…'}</span>
            </button>
          )}
        </div>
      )}

      {!currentUser ? null : loading ? (
        <div className="text-center py-8">
          <p className="text-gray-600">Loading active offers...</p>
        </div>
      ) : (
        <div className="tc-panel">
          {visibleTab === 'received' && (
            receivedOffers.length === 0 ? (
              <p className="tc-empty">No offers received.</p>
            ) : (
              <div className="tc-list">
                {receivedOffers.map((offer) => renderOfferBand(offer, 'received'))}
              </div>
            )
          )}

          {visibleTab === 'sent' && (
            sentOffers.length === 0 ? (
              <p className="tc-empty">No offers sent.</p>
            ) : (
              <div className="tc-list">
                {sentOffers.map((offer) => renderOfferBand(offer, 'sent'))}
              </div>
            )
          )}

          {/* Interests get their own tab rather than being mixed into the offer
              lists: an interest is not an offer, and presenting it as one sets
              the wrong expectation for both sides. The card stays the compact
              two-row link it always was, for the same reason. */}
          {visibleTab === 'interests' && (
            interestReplies.length === 0 && interestSignals.length === 0 ? (
              <p className="tc-empty">
                No interest yet. When someone flags interest in one of your listings, it lands here.
              </p>
            ) : (
              <>
                {interestReplies.length > 0 && (
                  <div style={{ marginBottom: interestSignals.length > 0 ? '1.75rem' : 0 }}>
                    <h3 className="tc-section-title">
                      Ready for an offer <span className="tc-section-count">({interestReplies.length})</span>
                    </h3>
                    <p className="tc-section-sub">Members who responded to your interest.</p>
                    {/* Deliberately not a gallery of the pedals they picked: a
                        pick of four or five would blow the card up, and the
                        offer page shows them full size anyway. This only has to
                        say an answer arrived and get them there. */}
                    <div className="tc-list">
                      {interestReplies.map((reply) => {
                        const targetName = [reply.listingSnapshot?.make, reply.listingSnapshot?.model]
                          .filter(Boolean).join(' ') || 'their pedal';
                        const count = reply.responsePedalIds.length;
                        const dateStr = reply.respondedAt?.toDate
                          ? reply.respondedAt.toDate().toLocaleDateString('en-US', {
                              month: 'short', day: 'numeric', year: 'numeric'
                            })
                          : '';
                        return (
                          <Link
                            key={reply.id}
                            data-restore-anchor={reply.id}
                            to={`/direct-offer?targetPedalId=${reply.listingId}&interestId=${reply.id}`}
                            onClick={saveViewState}
                            style={{
                              display: 'flex', alignItems: 'center', gap: '12px',
                              backgroundColor: 'white', borderRadius: '12px', padding: '12px',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.08)', textDecoration: 'none'
                            }}
                          >
                            <img
                              className="interest-signal-thumb"
                              src={reply.listingSnapshot?.imageUrl || reply.listingSnapshot?.placeholderUrl || SKETCH_PLACEHOLDER_URL}
                              alt={targetName}
                              style={{ width: '56px', height: '56px', objectFit: 'cover', borderRadius: '8px', flexShrink: 0 }}
                            />
                            <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                              <div style={{ color: '#6b7280', fontSize: '0.95rem' }}>
                                <span style={{ fontWeight: '700', color: '#111827' }}>{reply.ownerName}</span>
                                {' '}would trade their {targetName} for{' '}
                                {count === 1 ? 'one of your pedals' : `${count} of your pedals`}
                              </div>
                              <div className="interest-signal-meta" style={{
                                display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
                                gap: '12px', marginTop: '2px'
                              }}>
                                <span style={{ color: '#9ca3af', fontSize: '0.7rem' }}>{dateStr}</span>
                                <span style={{ color: '#5A9ECB', fontSize: '0.975rem', whiteSpace: 'nowrap' }}>
                                  Make an offer →
                                </span>
                              </div>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                )}

                {interestSignals.length > 0 && (
                  <div>
                    <h3 className="tc-section-title">
                      Interested in your gear <span className="tc-section-count">({interestSignals.length})</span>
                    </h3>
                    <p className="tc-section-sub">Members who want your gear.</p>
                    <div className="tc-list">
                      {interestSignals.map((signal) => {
                        const pedalName = [signal.listingSnapshot?.make, signal.listingSnapshot?.model]
                          .filter(Boolean).join(' ') || 'your pedal';
                        const dateStr = signal.createdAt?.toDate
                          ? signal.createdAt.toDate().toLocaleDateString('en-US', {
                              month: 'short', day: 'numeric', year: 'numeric'
                            })
                          : '';
                        return (
                          <Link
                            key={signal.id}
                            data-restore-anchor={signal.id}
                            to={`/interest/${signal.id}`}
                            onClick={saveViewState}
                            style={{
                              display: 'flex', alignItems: 'center', gap: '12px',
                              backgroundColor: 'white', borderRadius: '12px', padding: '12px',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.08)', textDecoration: 'none'
                            }}
                          >
                            <img
                              className="interest-signal-thumb"
                              src={signal.listingSnapshot?.imageUrl || signal.listingSnapshot?.placeholderUrl || SKETCH_PLACEHOLDER_URL}
                              alt={pedalName}
                              style={{ width: '56px', height: '56px', objectFit: 'cover', borderRadius: '8px', flexShrink: 0 }}
                            />
                            <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                              {/* Username carries the weight, the rest of the
                                  sentence reads as body copy — same treatment as
                                  the offer band headers. */}
                              <div style={{ color: '#6b7280', fontSize: '0.95rem' }}>
                                <span style={{ fontWeight: '700', color: '#111827' }}>{signal.senderName}</span>
                                {' '}is interested in your {pedalName}
                              </div>
                              {/* Date and link share a line so the card stays two
                                  rows tall at any width, rather than the link
                                  claiming a row of its own once it no longer
                                  fits beside the sentence. */}
                              <div className="interest-signal-meta" style={{
                                display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
                                gap: '12px', marginTop: '2px'
                              }}>
                                <span style={{ color: '#9ca3af', fontSize: '0.7rem' }}>{dateStr}</span>
                                <span style={{ color: '#5A9ECB', fontSize: '0.975rem', whiteSpace: 'nowrap' }}>
                                  See their pedals →
                                </span>
                              </div>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )
          )}
        </div>
      )}


      {/* Fixed gap below content */}
      <div style={{ height: '200px' }}></div>
      </div>
      </div>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default TradeCenter;